import { IPlace, Button, DateTimeInput, Input, InputContainer, PlaceInput } from '../../../libs';
import dayjs from 'dayjs';
import * as EmailValidator from 'email-validator';
import { useMemo, useState } from 'react';
import styled, { css } from 'styled-components';
import { mainApi, s3urls } from '../../../helpers/api';
import { usePricesStore } from '../../../store/usePricesStore';
import { LocationIcon, LogoIcon } from '../assets/icons/system';
import { Error } from '../helpers/errors';
import SuccessInfo from './SuccessInfo';
import { getNatalLink, getPartnerToken } from '../helpers/api';
import { googleEvent } from '../../../helpers/metrics';
import { useTranslation } from '../../../i18n/useTranslation';
import i18n from '../../../i18n/i18n';

// import ReRecaptcha, { TReReCaptchaApi } from '../../../components/ReRecaptcha';

interface FormProps {
  token: string;
  partner: string;
  setError(value: Error): void;
}

export default function Form(props: FormProps) {
  const defaultDate = dayjs().toISOString();
  const defaultTime = dayjs().utc().set('hour', 12).set('minute', 0).set('second', 0).toISOString();

  const [name, setName] = useState('');
  const [email, setEmail] = useState('');
  const [date, setDate] = useState<string>(defaultDate);
  const [time, setTime] = useState<string>(defaultTime);
  const [place, setPlace] = useState<IPlace | null>(null);

  const [nameMessage, setNameMessage] = useState<string | undefined>();
  const [emailMessage, setEmailMessage] = useState<string | undefined>();
  const [timeMessage, setTimeMessage] = useState<string | undefined>();
  const [dateMessage, setDateMessage] = useState<string | undefined>();
  const [placeMessage, setPlaceMessage] = useState<string | undefined>();

  const [payedStatus, setPayedStatus] = useState<'success' | 'failed'>();

  // const captchaRef = React.useRef<TReReCaptchaApi | null>(null);

  const price = usePricesStore().prices?.finance_capacity || 0;

  const { t } = useTranslation();

  const isDisabled = useMemo((): boolean => {
    return Boolean(
      nameMessage ||
        !name ||
        emailMessage ||
        !email ||
        timeMessage ||
        !time ||
        dateMessage ||
        !date ||
        placeMessage ||
        !place,
    );
  }, [nameMessage, name, emailMessage, email, timeMessage, time, dateMessage, date, placeMessage, place]);

  const onNameChange = (name: string) => {
    const resultName = name.trim();
    setName(name);
    if (/^[a-z,а-я,ё,\s]*$/i.test(resultName)) {
      setNameMessage(resultName.length === 0 ? t("chronos.indi.personalForm.nameIsNoEmpty") : undefined);
    } else {
      setNameMessage(t("chronos.indi.personalForm.justSimbolsInName"));
    }
  };

  const onEmailChange = (email: string) => {
    setEmail(email);
    setEmailMessage(EmailValidator.validate(email) || email === '' ? undefined : t("chronos.indi.fields.inputCorrectEmail"));
  }

  const onDateChange = (isoDate?: string) => {
    const message = t("chronos.app.instruments.widgets.personalForms.needDate")
    if (isoDate) {
      setDate(isoDate);
      setDateMessage(dayjs(isoDate) < dayjs('1900-01-01') ? t("chronos.app.rectification.inputValidDate") : dayjs(isoDate) > dayjs() ? t("chronos.indi.personalForm.inputDateInPast") : undefined);
    } else {
      setDateMessage(message);
    }
  };

  const onTimeChange = (isoTime?: string) => {
    const message = t("chronos.indi.personalForm.needsInputTime")

    if (isoTime) {
      setTime(isoTime);
      setTimeMessage(undefined);
    } else {
      setTimeMessage(message);
    }
  };

  const onPlaceChange = (place: IPlace | string | null) => {
    if (typeof place === 'object') {
      setPlace(place);
      setPlaceMessage(undefined);
    } else {
      setPlace(place => ({ ...place!, lat: 0, lon: 0 }) );
      setPlaceMessage(t("chronos.indi.personalForm.choosePlaseOnList"));
    }
  };

  const checkErrorMode = (val: any) => (Boolean(val) ? 'error' : 'normal');

  const handleSubmit = async () => {
    const data = {
      name: name,
      date: dayjs.utc(date).format('DD.MM.YYYY'),
      time: dayjs.utc(time).format('HH:mm:ss'),
      place: place?.name!,
      lat: place?.lat!,
      lon: place?.lon!,
      email,
      link: window.location.href,
      token: getPartnerToken(props.partner)
    };

    const isEnglish = i18n.language === 'en'
    const kind = isEnglish ? 'web' : 'telegram'
    const link = await getNatalLink(data, kind)
    if (!link) return

    googleEvent(isEnglish ? 'free_natal_successful_eng' : 'free_natal_successful')

    const finalLink = isEnglish ? link : `${link}?&${window.location.search.substring(1)}`
    if (isEnglish) {
      const tab = window.open(finalLink, '_blank')
      if (!tab) window.location.href = finalLink;
    } else {
      window.location.href = finalLink; 
    }
  };

  if (payedStatus === 'success') {
    return (
      <Container>
        <Content payedStatus={payedStatus}>
          <SuccessInfo email={email!} />
        </Content>
      </Container>
    );
  }

  const getLinks = () => {
      switch (i18n.language) {
        case 'en':
          return <>
          <Link href={s3urls.termsOfUseEN} target='_blank' rel='noreferrer'>
            {t("chronos.app.instruments.widgets.natalMapSun.Form.termsOfUse")}
          </Link>
          &nbsp;{t("chronos.app.and")}&nbsp;
          <Link href={s3urls.privacyPolicyEN} target='_blank' rel='noreferrer'>
            {t("chronos.app.instruments.widgets.natalMapSun.Form.privacyPolicy")}
          </Link>
        </>
        default:
          return <>
          <Link href={s3urls.termsOfUseRU} target='_blank' rel='noreferrer'>
            {t("chronos.app.instruments.widgets.natalMapSun.Form.termsOfUse")}
          </Link>
          &nbsp;{t("chronos.app.and")}&nbsp;
          <Link href={s3urls.privacyPolicyRU} target='_blank' rel='noreferrer'>
            {t("chronos.app.instruments.widgets.natalMapSun.Form.privacyPolicy")}
          </Link>
        </>
      }
    };

    const getTelegramInfo = () => {
      switch (i18n.language) {
        case 'en':
          return t("chronos.app.instruments.widgets.natalTG.Form.telegramInfoWithoutTG")
        default:
          return t("chronos.app.instruments.widgets.natalTG.Form.telegramInfo")
      }
    };

  return (
    <Container>
      <Header>
        <Logo><LogoIcon /></Logo>
        <Title1>{t("chronos.app.instruments.widgets.natalTG.Form.header.title1")}</Title1>
        <Title2>{t("chronos.app.instruments.widgets.natalTG.Form.header.title2")}</Title2>
        <Title3>{t("chronos.app.instruments.widgets.natalTG.Form.header.title3")}</Title3>
      </Header>

      <FormContainer>
        <InputContainer label={t("chronos.app.components.editForm.mainGroup.name")} message={nameMessage}>
          <StyledInput
            name="username"
            size="small"
            value={name}
            onChange={onNameChange}
            placeholder={t("chronos.app.components.onboarding.welcomeInterface.name.wantToNowYouCloser.whatIsYourName")}
            mode={checkErrorMode(nameMessage)}
          />
        </InputContainer>

        <InputContainer label={t("chronos.auth.signIn.email")} message={emailMessage}>
          <StyledInput
            name="email"
            size="small"
            value={email}
            onChange={onEmailChange}
            mode={checkErrorMode(emailMessage)}
            placeholder={t("chronos.app.instruments.widgets.natalMapSun.Form.emailMessage")}
          />
        </InputContainer>

        <InputContainer label={t("chronos.app.components.zet.birthTime")} message={timeMessage}>
          <StyledDateTimeInput
            size="small"
            type="time"
            value={time}
            hideIcon={true}
            onChange={onTimeChange}
            onInvalid={onTimeChange}
            mode={checkErrorMode(timeMessage)}
            utcMode
          />
        </InputContainer>

        <InputContainer label={t("chronos.app.components.zet.birthDate")} message={dateMessage}>
          <StyledDateTimeInput
            size="small"
            type="date"
            value={date}
            onChange={onDateChange}
            onInvalid={onDateChange}
            hideIcon={true}
            mode={checkErrorMode(dateMessage)}
            utcMode
          />
        </InputContainer>

        <InputContainer label={t("chronos.app.components.zet.birthPlace")} message={placeMessage}>
          <StyledPlaceInput
            size="small"
            value={place?.name || ''}
            onSelect={onPlaceChange}
            onChange={onPlaceChange}
            placeholder={t("chronos.app.instruments.widgets.natalMapSun.Form.placeMessage")}
            asyncDataFn={mainApi.places.bind(mainApi)}
            asyncDetailFn={mainApi.place.bind(mainApi)}
            mode={checkErrorMode(placeMessage)}
            lang={i18n.language}
            rightIcon={<LocationIcon/>}
            t={t}
          />
        </InputContainer>

        <TelegramInfo>{getTelegramInfo()}</TelegramInfo>

        <StyledButton
          name="submit-button"
          color="blue"
          onClick={handleSubmit}
          disabled={isDisabled}
        >
          {t("chronos.app.instruments.widgets.natalTG.Form.submit")}
        </StyledButton>
      </FormContainer>

      <Footer>
        <span>
          {t("chronos.app.instruments.widgets.natalTG.Form.submit2")} <br />
          {getLinks()}
        </span>
      </Footer>
    </Container>
  );
}

const Container = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;

  height: 100vh;
`;

const FormContainer = styled.div`
  width: 42.5rem;
  max-width: 85%;
  font-size: 0.875rem;
  margin: 1.5rem 0;

  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 1.25rem;

  & > :nth-child(5),
  & > :nth-child(n + 6) {
    grid-column: 1 / 3;
  }

  @media (max-width: 768px) {
    & > :nth-child(1) {
      grid-column: 1 / 3;
    }

    & > :nth-child(2) {
      grid-column: 1 / 3;
    }
  }
`;

const StyledInput = styled(Input)<{ error?: boolean }>`
  ${(props) =>
    props.error &&
    css`
      input {
        border-color: var(--red-color);
      }
    `};

  @media (max-width: 768px) {
    font-size: 1rem;
  }
`;

const StyledDateTimeInput = styled(DateTimeInput)<{ error?: boolean }>`
  ${(props) =>
    props.error &&
    css`
      border-color: var(--red-color);
    `};

  @media (max-width: 768px) {
    font-size: 1rem;
  }
`;

const StyledPlaceInput = styled(PlaceInput)<{ error?: boolean }>`
  @media (max-width: 768px) {
    font-size: 1rem;
  }
`;

const StyledButton = styled(Button)`
  color: white !important;
  background: rgba(117, 87, 242, 1) !important;
  font-size: 1.25rem;
  cursor: pointer;
`;

const Footer = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  color: var(--gray-color);
  font-size: 1rem;
  padding-bottom: 3rem;

  @media (max-width: 768px) {
    font-size: 0.8rem;
    max-width: 90%;
  }
`;

const Link = styled.a`
  color: var(--blue-color) !important;
`;

const Content = styled.div<{ sidebar?: string; payedStatus?: string }>`
  width: 100%;
  height: 100%;
  overflow: auto;
  display: flex;
  flex-direction: column;
  justify-content: center;
  text-align: center;

  @media (max-width: 1280px) {
    height: 100%;
    ::-webkit-scrollbar {
      width: 0px;
    }
  }

  @media (max-width: 768px) {
    width: 100vw !important;
  }
`;

const TelegramInfo = styled.div`
  background: #f2f2f2;
  border: 1px solid #d0d0d033;
  padding: 1rem;
  text-align: left;
  border-radius: 0.75rem;
`;

const Header = styled.div`
  max-width: 85%;
`;

const Logo = styled.div`
  margin-top: 1.5rem;
  margin-bottom: 3rem;

  path:nth-last-child(2) {
    fill: rgba(117, 87, 242, 1);
  }
`;

const Title1 = styled.div`
  font-size: 3rem;
  line-height: 3.75rem;
  letter-spacing: -0.022em;
  color: rgba(117, 87, 242, 1);
  margin-bottom: 1.25rem;
`;

const Title2 = styled.div`
  font-size: 1.25rem;
  line-height: 1.75rem;
  letter-spacing: -0.022em;
  color: #000000;
  margin-bottom: 2.5rem;
`;

const Title3 = styled.div`
  font-size: 1rem;
  line-height: 1.25rem;
  color: #7b7b7b;
`;
